import _ from 'lodash';
import type { ApolloCache, Cache, FetchResult } from '@apollo/client';
import updateApolloList from './updateApolloList';
import type { IdentifyFunction } from './updateApolloList';
import type { DataKeysForFetchResult, PathsToArrayValues } from './typeUtils';

type OrArray<T> = T | T[];

type QueryListUpdaterFunction<TData> = (
  cache: ApolloCache<unknown>,
  result: FetchResult<TData>,
) => void;

function internalUpdateQueryList<TData, QueryType, TVariables>(
  readQueryOptions: Cache.ReadQueryOptions<QueryType, TVariables>,
  pathToUpdate: OrArray<PathsToArrayValues<NonNullable<QueryType>>>,
  keyFromResponse: DataKeysForFetchResult<TData>,
  add: boolean,
  getIdentify?: (cache: ApolloCache<unknown>) => IdentifyFunction,
): QueryListUpdaterFunction<TData> {
  return (cache, { data }) => {
    const identify = getIdentify?.(cache);
    try {
      if (!data) return;
      const prev = cache.readQuery(readQueryOptions);
      if (!prev) return;

      const next = _.castArray(pathToUpdate).reduce(
        (updated, path) =>
          updateApolloList(updated, data, path, keyFromResponse, add, identify),
        prev,
      );
      cache.writeQuery({ ...readQueryOptions, data: next });
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn(e);
      }
    }
  };
}

export const updateQueryList = <TData, QueryType, TVariables>(
  readQueryOptions: Cache.ReadQueryOptions<QueryType, TVariables>,
  pathToUpdate: OrArray<PathsToArrayValues<NonNullable<QueryType>>>,
  keyFromResponse: DataKeysForFetchResult<TData>,
  add: boolean,
) =>
  internalUpdateQueryList(
    readQueryOptions,
    pathToUpdate,
    keyFromResponse,
    add,
    (cache) => (item) => (item ? cache.identify(item) : undefined),
  );

/**
 * @deprecated Use `updateQueryList` instead.
 *
 * This function behaves similarly as `updateQueryList` but
 * it uses `id` as the identifier for the items, instead of
 * respecting the type policies.
 */
export const legacyUpdateQueryList = <TData, QueryType, TVariables>(
  readQueryOptions: Cache.ReadQueryOptions<QueryType, TVariables>,
  pathToUpdate: OrArray<PathsToArrayValues<NonNullable<QueryType>>>,
  keyFromResponse: DataKeysForFetchResult<TData>,
  add = true,
) =>
  internalUpdateQueryList(readQueryOptions, pathToUpdate, keyFromResponse, add);
